import { type ISelectProps, Select, SubText } from '@procurify/ui'
import {
  components as ReactSelectComponents,
  type OptionProps,
  type SingleValueProps,
} from 'react-select'

import { useFormatTaxPercentage, useProcurifyIntl } from '@webapp/hooks'
import { type Tax } from '@webapp/platform/finance/entities/Tax'
import messages from './messages'
import { useTaxSelect } from './useTaxSelect'

export interface ITaxSelectProps extends ISelectProps {
  defaultTaxId?: number
}

export const TaxSelect = ({ defaultTaxId, ...props }: ITaxSelectProps) => {
  const intl = useProcurifyIntl()
  const formatTaxPercentage = useFormatTaxPercentage()

  const Option = (optionProps: OptionProps<Tax, false>) => {
    const { data, isSelected }: { data: Tax; isSelected: boolean } = optionProps

    return (
      <ReactSelectComponents.Option {...optionProps}>
        <SubText textEllipsis autoMargin={false}>
          {data.name}
        </SubText>
        <SubText
          color={isSelected ? undefined : 'textSecondary'}
          autoMargin={false}
        >
          {formatTaxPercentage(data.percentage)}
        </SubText>
        {data.isDefault && (
          <SubText color={isSelected ? undefined : 'textSecondary'}>
            {intl.formatMessage(messages.defaultTaxLabel)}
          </SubText>
        )}
      </ReactSelectComponents.Option>
    )
  }

  const SingleValue = (singleValueProps: SingleValueProps<Tax, false>) => {
    const { data }: { data: Tax } = singleValueProps

    return (
      <ReactSelectComponents.SingleValue {...singleValueProps}>
        <SubText autoMargin={false}>{data.name}</SubText>
        <SubText color='textSecondary'>
          {formatTaxPercentage(data.percentage)}
        </SubText>
      </ReactSelectComponents.SingleValue>
    )
  }

  const {
    data: { taxes, defaultValue },
    states: { isFetching },
  } = useTaxSelect({ defaultTaxId })

  return (
    <Select<Tax>
      components={{
        Option,
        SingleValue,
      }}
      options={taxes}
      isLoading={isFetching}
      getOptionLabel={(opt) => opt.name}
      getOptionValue={(opt) => opt.id?.toString()}
      placeholder={intl.formatMessage(messages.selectPlaceholderText)}
      defaultValue={defaultValue}
      {...props}
    />
  )
}
